import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Row, Col, Breadcrumb, Card, Tag, Typography, Empty, Spin,
    Space, Progress, message
} from "antd";
import {
    HomeOutlined, ThunderboltOutlined, ArrowRightOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const HARVESTB_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Completed": "green",
};

const HarvestCollection = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchBatches = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.post("/harvesting/ongoing_batches.php", {});
            setBatches(res.data.dataset || []);
        } catch {
            message.error("Failed to load ongoing harvest batches.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBatches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const collectionPercent = (batch) => {
        const targetKg = parseFloat(batch.target_kg || 0);
        const collectedKg = parseFloat(batch.total_collect_kg || 0);
        if (targetKg <= 0) return 0;
        return Math.min(100, Math.round((collectedKg / targetKg) * 100));
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Harvesting' },
                                { title: 'Harvest Collection' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ThunderboltOutlined /> Harvest Collection
                        </h1>
                        <p className="fluid-page-subtitle">
                            Select an ongoing harvest batch to record harvests and rejections
                        </p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <Space>
                            <Typography.Text type="secondary">
                                {batches.length} ongoing batch{batches.length === 1 ? "" : "es"}
                            </Typography.Text>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">Ongoing Harvest Batches</h3>
                    </div>
                    <Spin spinning={loading}>
                        {!loading && batches.length === 0 ? (
                            <Empty
                                description="No ongoing harvest batches found"
                                style={{ padding: "40px 0" }}
                            />
                        ) : (
                            <Row gutter={[16, 16]}>
                                {batches.map((b) => (
                                    <Col xs={24} sm={12} lg={8} xl={6} key={b.harvestb_id}>
                                        <Card
                                            hoverable
                                            className="fluid-collection-card"
                                            onClick={() => navigate(`/harvesting/harvest-collection/${b.harvestb_id}`)}
                                            style={{ height: "100%" }}
                                        >
                                            <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
                                                <Typography.Text strong style={{ fontSize: 15 }}>
                                                    {b.harvestb_ref}
                                                </Typography.Text>
                                                <Tag color={HARVESTB_STATUS_COLORS[b.harvestb_status] || "default"} style={{ fontWeight: 600 }}>
                                                    {b.harvestb_status}
                                                </Tag>
                                            </Space>
                                            <Typography.Text type="secondary" style={{ display: "block" }}>
                                                {b.land_name || "-"}
                                                {b.section_name ? `  /  ${b.section_name}` : ""}
                                            </Typography.Text>
                                            <Typography.Text style={{ display: "block", marginTop: 4 }}>
                                                Season: <Tag>{b.harvest_season || "-"}</Tag>
                                            </Typography.Text>
                                            <div style={{ marginTop: 10 }}>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    Target: {Number(b.target_qty || 0).toLocaleString()} qty /{" "}
                                                    {Number(b.target_kg || 0).toLocaleString()} kg
                                                </Typography.Text>
                                            </div>
                                            <div>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    Collected: {Number(b.total_collect_qty || 0).toLocaleString()} qty /{" "}
                                                    {Number(b.total_collect_kg || 0).toLocaleString()} kg
                                                </Typography.Text>
                                            </div>
                                            <Progress
                                                percent={collectionPercent(b)}
                                                size="small"
                                                strokeColor="#1677ff"
                                                style={{ marginTop: 8 }}
                                            />
                                            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    {b.harvest_start_date ? dayjs(b.harvest_start_date).format("YYYY-MM-DD") : "-"}
                                                    {" → "}
                                                    {b.harvest_end_date ? dayjs(b.harvest_end_date).format("YYYY-MM-DD") : "-"}
                                                </Typography.Text>
                                                <Typography.Text style={{ color: "#1677ff" }}>
                                                    Open <ArrowRightOutlined />
                                                </Typography.Text>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </Spin>
                </div>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default HarvestCollection;